import { browser } from '$app/environment';
import { PUBLIC_POSTHOG_KEY, PUBLIC_POSTHOG_HOST } from '$env/static/public';
import type { CaptureResult } from 'posthog-js';
import { getPostHog, whenReady } from './_posthog.client';

let initialized = false;

// Toggle to drop the entire "Non-Error promise rejection captured with value: …"
// category. These events have no stack trace by definition (PostHog synthesizes
// the wrapper because the rejected value isn't an Error instance) so they're
// rarely actionable. Off by default — leave events flowing while we observe
// volume; flip to true if the noise becomes a problem. The category surface
// includes browser extensions injecting page-world scripts, email link
// scanners (Outlook SafeLinks etc.), AV/DLP page rewriters, and third-party
// libraries that reject with strings rather than Error objects.
const dropNonErrorPromiseRejections = false;

// Drop well-known browser noise from $exception events before they're sent
// to PostHog Error Tracking. Conservative on purpose — only patterns that
// are unambiguously not bugs in our code.
function filterEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event || event.event !== '$exception') return event;
  const list = event.properties?.['$exception_list'] as
    | { value?: string; stacktrace?: { frames?: { filename?: string }[] } }[]
    | undefined;
  if (!list?.length) return event;
  for (const ex of list) {
    const message = ex.value ?? '';
    // Benign Chrome warning: layout work scheduled inside a ResizeObserver
    // callback. Spec-mandated, no action available, fires constantly on
    // some pages.
    if (message.startsWith('ResizeObserver loop')) return null;
    // Microsoft Outlook SafeLinks / Defender for Office 365 link scanner.
    // When an email containing a link to our site is delivered, Microsoft's
    // protection partially executes the page in a sandboxed MSHTML context
    // that emits a non-Error promise rejection with this exact COM-style
    // signature ("Object Not Found Matching Id:N, MethodName:update,
    // ParamCount:N"). The string isn't present in our source or any
    // installed dep — confirmed via grep and PostHog's own GitHub issue
    // #33050. Not real users, not actionable.
    if (message.includes('Object Not Found Matching Id:')) return null;
    // Broad fallback for unstacktraceable promise rejections (see flag
    // comment above). Gated so we can observe volume before committing to
    // the broader drop.
    if (dropNonErrorPromiseRejections && message.startsWith('Non-Error promise rejection captured with value:')) {
      return null;
    }
    // Browser extensions injecting scripts into the page. We don't ship
    // these and can't fix them. Note: this only catches events that have a
    // stack trace — non-Error promise rejections have empty `frames`, so the
    // two preceding checks are doing the work for that category.
    const frames = ex.stacktrace?.frames ?? [];
    if (frames.some((f) => /^(chrome|moz|safari-web)-extension:\/\//.test(f.filename ?? ''))) {
      return null;
    }
  }
  return event;
}

export async function initProduct(featureFlags: Record<string, string | boolean>) {
  if (!browser || initialized) return;

  await whenReady();
  const posthog = getPostHog();
  posthog?.init(PUBLIC_POSTHOG_KEY, {
    api_host: PUBLIC_POSTHOG_HOST,
    ui_host: 'https://us.posthog.com',
    defaults: '2026-01-30',
    capture_pageview: false,
    capture_pageleave: false,
    // Enable client-side exception autocapture so PostHog patches
    // window.onerror and window.onunhandledrejection. Without this,
    // browser-side errors are invisible to PostHog Error Tracking.
    capture_exceptions: true,
    before_send: filterEvent,
    bootstrap: { featureFlags },
    // Session replay — the entire PostHog SDK import is deferred to
    // requestIdleCallback (see _posthog.client.ts), so both the SDK eval
    // and the recorder script load happen off the critical path. Recording
    // is enabled at init time since we're already in idle.
    //
    // Masking rules:
    //   - `maskAllInputs: true` → every <input>, <textarea>, <select> is
    //     masked (passwords automatic, plus all free-text fields)
    //   - `maskTextSelector: '[data-ph-mask]'` → opt-in text masking escape
    //     hatch for elements that render sensitive content as plain text
    //     (not an input)
    //   - `blockSelector: '[data-ph-block]'` → opt-in full-block escape
    //     hatch for entire DOM subtrees we don't want captured at all
    //
    // Handle and display name are intentionally NOT masked — they are
    // public data per `docs/product-manager/subscription-plans.md` and the
    // onboarding flow. Privacy policy (`/privacy`) documents the recording
    // and user opt-out path.
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-ph-mask]',
      blockSelector: '[data-ph-block]',
    },
  });
  initialized = true;
}
